// agent-analyzer CLI (ARCHITECTURE.md §14).
//
// Commander wiring is hand-built for correct parsers and validation; types,
// policy, introspect, and extract use generated cli-contracts artifacts.

import { Command, CommanderError, InvalidArgumentError, Option } from "commander";

import { EDGE_CLASSIFICATIONS } from "../types.js";
import type { EdgeClassification } from "../types.js";
import { EVIDENCE_TYPES } from "../evidence/types.js";
import type { EvidenceType } from "../evidence/types.js";
import { CONTRACT_JSON_STR, CONTRACT_YAML } from "../generated/cli/contract.js";
import { deriveCommandPolicy } from "../generated/cli/policy.js";
import {
  handleCacheClear,
  handleCacheStatus,
  handleCallgraph,
  handleContextPack,
  handleDependencies,
  handleGraph,
  handleImpact,
  handleSemantic,
  type CommonOptions,
} from "./handlers.js";
import { OUTPUT_FORMATS } from "./output.js";

export * from "./output.js";
export * from "./project.js";
export * from "./git-diff.js";

// ---------------------------------------------------------------------------
// Common option access
// ---------------------------------------------------------------------------

function readCommon(command: Command): CommonOptions {
  const opts = command.optsWithGlobals() as Record<string, unknown>;
  return {
    projectRoot: (opts["projectRoot"] as string) ?? ".",
    dsl: opts["dsl"] as string | undefined,
    config: opts["config"] as string | undefined,
    format: (opts["format"] as CommonOptions["format"]) ?? "text",
    edgeTypes:
      (opts["edgeTypes"] as EdgeClassification[] | undefined) ?? [
        "deterministic",
        "candidate",
      ],
    evidenceExclude: (opts["evidenceExclude"] as EvidenceType[] | undefined) ?? [],
    limit: opts["limit"] as number | undefined,
    showRelevance: opts["showRelevance"] === true,
    cache: opts["cache"] !== false,
    quiet: opts["quiet"] === true,
  };
}

/** Parse a comma-separated `--edge-types` value into validated classifications. */
function parseEdgeTypes(value: string): EdgeClassification[] {
  const parts = value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  for (const part of parts) {
    if (!(EDGE_CLASSIFICATIONS as readonly string[]).includes(part)) {
      throw new InvalidArgumentError(
        `Unknown edge type '${part}'. Expected: ${EDGE_CLASSIFICATIONS.join(", ")}.`,
      );
    }
  }
  return parts as EdgeClassification[];
}

/** Parse a comma-separated `--evidence-exclude` value into validated types. */
function parseEvidenceTypes(value: string): EvidenceType[] {
  const parts = value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  for (const part of parts) {
    if (!(EVIDENCE_TYPES as readonly string[]).includes(part)) {
      throw new InvalidArgumentError(
        `Unknown evidence type '${part}'. Expected one of: ${EVIDENCE_TYPES.join(", ")}.`,
      );
    }
  }
  return parts as EvidenceType[];
}

function parseIntArg(value: string): number {
  const n = Number.parseInt(value, 10);
  if (Number.isNaN(n)) {
    throw new InvalidArgumentError("Not an integer.");
  }
  return n;
}

function maybeIntrospect(
  commandId: string,
  opts: Record<string, unknown>,
  command: Command,
): boolean {
  const globalOpts = command.optsWithGlobals() as Record<string, unknown>;
  if (globalOpts["introspect"] !== true) return false;
  const policy = deriveCommandPolicy(commandId, opts);
  process.stdout.write(`${JSON.stringify(policy, null, 2)}\n`);
  return true;
}

// ---------------------------------------------------------------------------
// extract (from generated program.ts; omit process.exit(0) for exitOverride)
// ---------------------------------------------------------------------------

function registerExtract(program: Command): void {
  program
    .command("extract")
    .description("Extract contract specification for this CLI tool.")
    .argument("[commands...]", "Command IDs to extract. Use dot notation.")
    .option("-a, --all", "Extract all commands.", false)
    .option("--include-meta", "Include extraction metadata.", true)
    .option("-F, --format <format>", "Output format (yaml or json).", "yaml")
    .action(async (commands: string[], opts, cmd: Command) => {
      if (commands.length === 0 && !opts.all) {
        process.stderr.write(
          `${JSON.stringify({
            code: "INVALID_ARGS",
            message: "Specify command IDs or use --all",
          })}\n`,
        );
        throw new CommanderError(2, "commander.invalidArgs", "");
      }

      const format = opts.format || "yaml";
      const doc = JSON.parse(CONTRACT_JSON_STR) as Record<string, unknown>;
      const cmdIds = opts.all ? [] : commands;

      if (cmdIds.length === 0) {
        if (format === "json") {
          const out: Record<string, unknown> = {};
          if (opts.includeMeta) {
            out._meta = {
              source: "embedded",
              type: "cli-contracts/extract",
              extractedAt: new Date().toISOString(),
              specVersion: doc.cli_contracts ?? "0.1.0",
              commands: [
                "agent-analyzer.graph",
                "agent-analyzer.impact",
                "agent-analyzer.callgraph",
                "agent-analyzer.context-pack",
                "agent-analyzer.dependencies",
                "agent-analyzer.semantic",
                "agent-analyzer.cache",
              ],
            };
          }
          Object.assign(out, doc);
          process.stdout.write(`${JSON.stringify(out, null, 2)}\n`);
        } else {
          const yamlLines: string[] = [];
          yamlLines.push("# agent-analyzer extract");
          yamlLines.push("# source: embedded");
          yamlLines.push("# type: cli-contracts/command-extract");
          if (opts.includeMeta) {
            yamlLines.push("---");
            yamlLines.push("source: embedded");
            yamlLines.push("type: cli-contracts/command-extract");
            yamlLines.push(`extractedAt: ${new Date().toISOString()}`);
            yamlLines.push(`spec_version: ${String(doc.cli_contracts ?? "0.1.0")}`);
            yamlLines.push("commands:");
            for (const id of [
              "agent-analyzer.graph",
              "agent-analyzer.impact",
              "agent-analyzer.callgraph",
              "agent-analyzer.context-pack",
              "agent-analyzer.dependencies",
              "agent-analyzer.semantic",
              "agent-analyzer.cache",
            ]) {
              yamlLines.push(`  - ${id}`);
            }
          }
          yamlLines.push("---");
          yamlLines.push(CONTRACT_YAML);
          process.stdout.write(`${yamlLines.join("\n")}\n`);
        }
      } else {
        const filtered: Record<string, unknown> = {
          cli_contracts: doc.cli_contracts,
          info: doc.info,
          command_sets: {},
        };
        const fcs = filtered.command_sets as Record<
          string,
          Record<string, unknown>
        >;
        for (const [setId, cs] of Object.entries(
          (doc.command_sets ?? {}) as Record<string, unknown>,
        )) {
          const cmds = (cs as Record<string, unknown>).commands as
            | Record<string, unknown>
            | undefined;
          if (!cmds) continue;
          const matched: Record<string, unknown> = {};
          for (const [cmdId, cmdDef] of Object.entries(cmds)) {
            const fullId = `${setId}.${cmdId}`;
            if (
              cmdIds.some(
                (id) => id === cmdId || id === fullId || cmdId.startsWith(`${id}.`),
              )
            ) {
              matched[cmdId] = cmdDef;
            }
          }
          if (Object.keys(matched).length > 0) {
            const setCopy = { ...(cs as Record<string, unknown>) };
            setCopy.commands = matched;
            fcs[setId] = setCopy;
          }
        }
        if (doc.components) filtered.components = doc.components;
        process.stdout.write(`${JSON.stringify(filtered, null, 2)}\n`);
      }
    });
}

/** Build the agent-analyzer commander program with all commands registered. */
export function buildProgram(): Command {
  const program = new Command();

  program
    .name("agent-analyzer")
    .description(
      "Source-aware analysis for the agent-contracts ecosystem",
    )
    .option("--project-root <path>", "project root directory", ".")
    .option(
      "--dsl <path>",
      "path to agent-contracts.yaml (default: auto-detect)",
    )
    .option("--config <path>", "path to agent-contracts.config.yaml")
    .addOption(
      new Option("--format <format>", "output format")
        .choices([...OUTPUT_FORMATS])
        .default("text"),
    )
    .option(
      "--edge-types <list>",
      "comma-separated edge classifications to traverse",
      parseEdgeTypes,
      ["deterministic", "candidate"],
    )
    .option(
      "--evidence-exclude <list>",
      "comma-separated evidence types to exclude from relevance scoring",
      parseEvidenceTypes,
      [],
    )
    .option(
      "--limit <n>",
      "maximum number of results (impact, context-pack)",
      parseIntArg,
    )
    .option("--show-relevance", "include relevance scores in text output")
    .option("--no-cache", "disable the persistent cache")
    .option("--quiet", "suppress informational output")
    .option(
      "--introspect",
      "output derived policy as JSON without executing the command",
    );

  program
    .command("graph")
    .description("Build and output the project dependency graph")
    .action(async (_opts, command: Command) => {
      if (maybeIntrospect("graph", {}, command)) return;
      await handleGraph(readCommon(command));
    });

  program
    .command("impact")
    .description("Analyze the impact of changed files, symbols, or an artifact")
    .option("--files <paths...>", "changed file paths")
    .option("--symbols <ids...>", "changed symbol ids (filePath#name)")
    .option("--artifact <id>", "analyze impact of an artifact's files")
    .option(
      "--diff <ref>",
      "derive changed symbols from a git diff against <ref> (e.g. HEAD~1, main)",
    )
    .addOption(
      new Option("--direction <direction>", "traversal direction")
        .choices(["downstream", "upstream"])
        .default("downstream"),
    )
    .option("--max-depth <n>", "maximum traversal depth", parseIntArg)
    .action(async (opts, command: Command) => {
      if (maybeIntrospect("impact", opts, command)) return;
      await handleImpact(readCommon(command), {
        files: opts.files as string[] | undefined,
        symbols: opts.symbols as string[] | undefined,
        artifact: opts.artifact as string | undefined,
        diff: opts.diff as string | undefined,
        direction: opts.direction as "downstream" | "upstream",
        maxDepth: opts.maxDepth as number | undefined,
      });
    });

  program
    .command("callgraph")
    .description("Build a call graph rooted at a symbol by following calls edges")
    .requiredOption("--entry <symbolId>", "root symbol id (filePath#name)")
    .option("--depth <n>", "maximum traversal depth", parseIntArg, 3)
    .addOption(
      new Option("--direction <direction>", "traversal direction")
        .choices(["forward", "reverse"])
        .default("forward"),
    )
    .action(async (opts, command: Command) => {
      if (maybeIntrospect("callgraph", opts, command)) return;
      // Output format comes from the global --format (json | text | dot);
      // anything else (e.g. yaml) renders as the text tree.
      const common = readCommon(command);
      const format =
        common.format === "json"
          ? "json"
          : common.format === "dot"
            ? "dot"
            : "text";
      await handleCallgraph(common, {
        entry: opts.entry as string,
        depth: opts.depth as number,
        format,
        direction: opts.direction as "forward" | "reverse",
      });
    });

  program
    .command("context-pack")
    .description("Generate a token-budgeted context pack for the changed files")
    .option("--files <paths...>", "changed file paths")
    .option("--task <id>", "task identifier recorded in the pack")
    .option("--budget <tokens>", "token budget", parseIntArg, 50_000)
    .addOption(
      new Option(
        "--granularity <mode>",
        "context granularity: file (whole files) or symbol (symbol-scoped sections)",
      )
        .choices(["file", "symbol"])
        .default("symbol"),
    )
    .action(async (opts, command: Command) => {
      if (maybeIntrospect("context-pack", opts, command)) return;
      await handleContextPack(readCommon(command), {
        files: (opts.files as string[] | undefined) ?? [],
        task: opts.task as string | undefined,
        budget: opts.budget as number,
        granularity: opts.granularity as "file" | "symbol",
      });
    });

  program
    .command("dependencies")
    .description("List the dependencies of a file")
    .requiredOption("--file <path>", "file to inspect")
    .addOption(
      new Option("--direction <direction>", "dependency direction")
        .choices(["forward", "reverse"])
        .default("forward"),
    )
    .action(async (opts, command: Command) => {
      if (maybeIntrospect("dependencies", opts, command)) return;
      await handleDependencies(readCommon(command), {
        file: opts.file as string,
        direction: opts.direction as "forward" | "reverse",
      });
    });

  program
    .command("semantic")
    .description(
      "Run semantic (LLM) gap analysis to propose candidate edges",
    )
    .option("--adapter <name>", "LLM adapter to use (claude, openai, gemini, cursor)")
    .option("--model <name>", "LLM model to use (overrides adapter default)")
    .option(
      "--max-candidates <n>",
      "maximum candidate edges per run",
      parseIntArg,
    )
    .option("--dry-run", "detect and print gaps without calling the LLM")
    .action(async (opts, command: Command) => {
      if (maybeIntrospect("semantic", opts, command)) return;
      await handleSemantic(readCommon(command), {
        dryRun: opts.dryRun === true,
        adapter: opts.adapter as string | undefined,
        model: opts.model as string | undefined,
        maxCandidates: opts.maxCandidates as number | undefined,
      });
    });

  const cache = program
    .command("cache")
    .description("Inspect or clear the persistent analysis store");

  cache
    .command("status")
    .description("Show store statistics")
    .action(async (_opts, command: Command) => {
      if (maybeIntrospect("cache", { subcommand: "status" }, command)) return;
      await handleCacheStatus(readCommon(command));
    });

  cache
    .command("clear")
    .description("Delete the store file")
    .action(async (_opts, command: Command) => {
      if (maybeIntrospect("cache", { subcommand: "clear" }, command)) return;
      await handleCacheClear(readCommon(command));
    });

  registerExtract(program);

  return program;
}

/** Recursively make every command throw (rather than process.exit) on error. */
function applyExitOverride(command: Command): void {
  command.exitOverride();
  for (const sub of command.commands) applyExitOverride(sub);
}

/**
 * Parse `argv` (user arguments, without node/script) and return the process
 * exit code. Never throws; analysis errors map to 1, argument errors to 2.
 */
export async function run(
  argv: string[],
  program: Command = buildProgram(),
): Promise<number> {
  applyExitOverride(program);
  try {
    await program.parseAsync(argv, { from: "user" });
    return 0;
  } catch (error) {
    if (error instanceof CommanderError) {
      if (
        error.code === "commander.helpDisplayed" ||
        error.code === "commander.help" ||
        error.code === "commander.version"
      ) {
        return 0;
      }
      // Map all argument/usage errors to exit 2 (§14.3), regardless of
      // Commander's per-error exitCode (e.g. unknownCommand defaults to 1).
      return 2;
    }
    process.stderr.write(`Error: ${(error as Error).message}\n`);
    return 1;
  }
}
